import React from 'react';
import PropTypes from 'prop-types';
import { useLocaleContext } from '../context/LocaleContext';

const AppliedCouponsTable = ({ 
  couponObjects, 
  onUnapplyCoupon, 
  onDeleteCoupon,
  isCouponButtonVisible
}) => {
  const locale = useLocaleContext();
  const isFrench = locale === 'fr-CA';
  
  // Helper function to get locale-specific value from bilingual string
  const getLocaleValue = (bilingualString) => {
    if (!bilingualString) return '';
    const str = String(bilingualString);
    const arr = str.split(', ');
    let out = [];
    for (let i = 0; i < arr.length; i += 2) {
      if (arr[i] && arr[i + 1]) {
        out.push(isFrench ? arr[i + 1] : arr[i]);
      }
    }
    return out.join(', ');
  };
  
  const formatCurrency = (amount) => {
    const numAmount = typeof amount === 'number' ? amount : parseFloat(amount);
    if (isNaN(numAmount)) {
      return amount ? `$${amount}` : '$0.00';
    }

    const formatted = new Intl.NumberFormat(isFrench ? 'fr-CA' : 'en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2
    }).format(Math.abs(numAmount));

    return numAmount < 0 ? (isFrench ? `-${formatted}` : formatted.replace('$', '-$')) : formatted;
  };

  if (!couponObjects || couponObjects.length === 0) {
    return null;
  }

  return (
    <div style={{ marginTop: '16px' }}>
      {/* Purple Header Row */}
      <div style={{ 
        padding: '0.5rem 1.5rem', 
        backgroundColor: '#4b286d',
        borderRadius: '8px 8px 0 0'
      }}>
        <h4 style={{ 
          margin: '0', 
          fontSize: '1.2rem', 
          fontWeight: '400', 
          color: '#fff' 
        }}>
          {isFrench ? 'Coupons Appliqués' : 'Applied Coupons'}
        </h4>
      </div>
      
      <div style={{ 
        border: '1px solid #d9d9d9', 
        borderRadius: '0 0 8px 8px', 
        overflow: 'hidden',
        backgroundColor: '#fff'
      }}>
        {/* Table Header */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr 1fr 1fr 200px', 
          backgroundColor: '#f5f6f7',
          borderBottom: '1px solid #d9d9d9',
          fontWeight: '600',
          fontSize: '14px',
          color: '#2a2c2e'
        }}>
          <div style={{ padding: '12px 16px', borderRight: '1px solid #d9d9d9' }}>
            {isFrench ? 'Code' : 'Code'}
          </div>
          <div style={{ padding: '12px 16px', borderRight: '1px solid #d9d9d9' }}>
            {isFrench ? 'Type' : 'Type'}
          </div>
          <div style={{ padding: '12px 16px', borderRight: '1px solid #d9d9d9' }}>
            {isFrench ? 'Valeur' : 'Value'}
          </div>
          <div style={{ padding: '12px 16px', borderRight: '1px solid #d9d9d9' }}>
            {isFrench ? 'Durée' : 'Duration'}
          </div>
          <div style={{ padding: '12px 16px', textAlign: 'center' }}>
            {isFrench ? 'Actions' : 'Actions'}
          </div>
        </div>
        
        {/* Table Rows */}
        {couponObjects.map((coupon) => {
          const couponCode = (coupon.coupon && coupon.coupon.couponCode) || coupon.couponCode;
          const couponTypeRaw = (coupon.coupon && coupon.coupon.couponType) || coupon.couponType || 'General';
          const couponType = getLocaleValue(couponTypeRaw) || 'General';
          const dollarValue = (coupon.coupon && coupon.coupon.dollarValue) || coupon.dollarValue || coupon.promotionAmount;
          const durationRaw = (coupon.coupon && coupon.coupon.duration) || coupon.duration;
          const duration = getLocaleValue(durationRaw);
          const isApplied = coupon.isApplied === true; // Only true if explicitly set to true
          
          return (
            <div 
              key={`coupon-${couponCode}`}
              style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr 1fr 1fr 200px',
                borderBottom: '1px solid #e2e2e2',
                backgroundColor: '#fff'
              }}
            >
              <div style={{ 
                padding: '12px 16px', 
                borderRight: '1px solid #e2e2e2',
                fontSize: '14px',
                color: '#2a2c2e',
                fontWeight: '500'
              }}>
                {couponCode}
              </div>
              
              <div style={{ 
                padding: '12px 16px', 
                borderRight: '1px solid #e2e2e2',
                fontSize: '14px',
                color: '#2a2c2e'
              }}>
                {couponType}
              </div>
              
              <div style={{ 
                padding: '12px 16px', 
                borderRight: '1px solid #e2e2e2',
                fontSize: '14px',
                color: '#4b286d',
                fontWeight: '600'
              }}>
                {formatCurrency(dollarValue)}
              </div>
              
              <div style={{ 
                padding: '12px 16px', 
                borderRight: '1px solid #e2e2e2',
                fontSize: '14px',
                color: '#2a2c2e'
              }}>
                {duration || '-'}
              </div>
              
              <div style={{ 
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}>
                {isCouponButtonVisible && (
                  <>
                    <button
                      type="button"
                      onClick={() => onUnapplyCoupon(couponCode)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: isApplied ? '#4b286d' : '#28a745',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        textDecoration: 'underline',
                        padding: '0',
                        fontFamily: 'inherit'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.color = '#3a1f56';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = '#4b286d';
                      }}
                    >
                      {isApplied 
                        ? (isFrench ? 'Supprimer' : 'Unapply')
                        : (isFrench ? 'Appliquer' : 'Apply')
                      }
                    </button>
                    <span style={{ color: '#d9d9d9' }}>|</span>
                    <button
                      type="button"
                      onClick={() => onDeleteCoupon && onDeleteCoupon(couponCode)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#dc3545',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        textDecoration: 'underline',
                        padding: '0',
                        fontFamily: 'inherit'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.color = '#c82333';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = '#dc3545';
                      }}
                    >
                      {isFrench ? 'Supprimer définitivement' : 'Delete'}
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Description Section */}
      {couponObjects.some(coupon => {
        if (coupon.coupon && coupon.coupon.descInstallEn) {
          return true;
        }
        return false;
      }) && (
        <div style={{ marginTop: '16px' }}>
          <h5 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#4b286d' }}>
            {isFrench ? 'Descriptions des Coupons' : 'Coupon Descriptions'}
          </h5>
          {couponObjects.map((coupon) => {
            const couponCode = (coupon.coupon && coupon.coupon.couponCode) || coupon.couponCode;
            let description = '';
            if (coupon.coupon && coupon.coupon.descInstallEn) {
              description = isFrench && coupon.coupon.descInstallFr ? 
                coupon.coupon.descInstallFr : 
                coupon.coupon.descInstallEn;
            }
            
            if (description) {
              return (
                <div 
                  key={`desc-${couponCode}`}
                  style={{ 
                    marginBottom: '8px', 
                    padding: '12px', 
                    backgroundColor: '#f5f6f7', 
                    borderRadius: '4px',
                    borderLeft: '4px solid #4b286d'
                  }}
                >
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#4b286d', marginBottom: '4px' }}>
                    {couponCode}:
                  </div>
                  <div style={{ fontSize: '14px', color: '#2a2c2e', lineHeight: '1.4' }}>
                    {description}
                  </div>
                </div>
              );
            }
            return null;
          })}
        </div>
      )}
    </div>
  );
};

AppliedCouponsTable.propTypes = {
  couponObjects: PropTypes.arrayOf(PropTypes.shape({
    couponCode: PropTypes.string,
    couponType: PropTypes.string,
    dollarValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    duration: PropTypes.string,
    promotionAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    isApplied: PropTypes.bool,
    coupon: PropTypes.shape({
      couponCode: PropTypes.string,
      couponType: PropTypes.string,
      dollarValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      duration: PropTypes.string,
      descInstallEn: PropTypes.string,
      descInstallFr: PropTypes.string
    })
  })).isRequired,
  onUnapplyCoupon: PropTypes.func.isRequired,
  onDeleteCoupon: PropTypes.func,
  readonly: PropTypes.bool
};

export default AppliedCouponsTable;
